import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';

/** A pending vibe change, mirroring the backend `/pending-updates` payload. */
interface PendingUpdate {
  app: string;
  kind: 'update' | 'import';
  pullRequestUrl: string;
  pullRequestNumber: number;
  title: string;
  previewUrl: string;
  previewState: 'ready' | 'pending' | 'stale';
  headSha: string;
  verification: {
    rollup: 'pass' | 'pending' | 'fail';
    checks: Array<{
      name: string;
      state: 'pass' | 'pending' | 'fail';
      missing?: boolean;
    }>;
  };
  updatedAt: string;
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((Date.now() - then) / 1000);
  const ranges: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.34524, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];
  let duration = seconds;
  let unit: Intl.RelativeTimeFormatUnit = 'second';
  for (const [divisor, candidate] of ranges) {
    if (Math.abs(duration) < divisor) {
      unit = candidate;
      break;
    }
    duration /= divisor;
    unit = candidate;
  }
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  return rtf.format(-Math.round(duration), unit);
}

/**
 * Scaffolder field that lets a user pick which pending vibe-coded-app change to
 * promote, without touching GitHub. Fetches the requester's owner-scoped open
 * pull requests from the cloudflare-workers backend and renders them as a
 * dropdown; the selected value is the PR URL the merge action consumes. The
 * chosen change's preview link is shown inline so the user can review it first.
 */
export const PendingVibeUpdatePicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    schema: { title = 'Pending change to publish', description },
  } = props;

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [updates, setUpdates] = useState<PendingUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('cloudflare-workers');
        const res = await fetchApi.fetch(`${baseUrl}/pending-updates`);
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const body = (await res.json()) as { error?: string };
            if (body?.error) detail = body.error;
          } catch {
            // non-JSON body — keep the status detail
          }
          throw new Error(detail);
        }
        const body = (await res.json()) as { updates?: PendingUpdate[] };
        if (active) setUpdates(body.updates ?? []);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi]);

  const selected = useMemo(
    () => updates.find(u => u.pullRequestUrl === formData),
    [updates, formData],
  );

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={8} my={2}>
        <CircularProgress size={18} />
        <Typography variant="body2">Loading your pending changes…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <FormControl error fullWidth margin="normal">
        <Typography variant="subtitle2">{title}</Typography>
        <FormHelperText>
          Could not load your pending changes: {error}
        </FormHelperText>
      </FormControl>
    );
  }

  if (updates.length === 0) {
    return (
      <FormControl fullWidth margin="normal">
        <Typography variant="subtitle2">{title}</Typography>
        <FormHelperText>
          You have no pending changes to publish. Run “Update Your App” (or “Add
          Your App”) first, then come back here.
        </FormHelperText>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={Boolean(rawErrors?.length) && !formData}
    >
      <InputLabel id="pending-vibe-update-label">{title}</InputLabel>
      <Select
        labelId="pending-vibe-update-label"
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
      >
        {updates.map(u => (
          <MenuItem
            key={u.pullRequestUrl}
            value={u.pullRequestUrl}
            disabled={u.verification.rollup !== 'pass'}
          >
            {u.app}
            {u.kind === 'import' ? ' (first deploy)' : ''} — updated{' '}
            {formatRelativeTime(u.updatedAt)}
            {u.verification.rollup !== 'pass' ? ' — checks not ready' : ''}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {description ??
          'Pick the change you want to publish. Review its preview below first.'}
      </FormHelperText>
      {selected && (
        <Box mt={1}>
          {(() => {
            if (selected.previewState === 'ready' && selected.previewUrl) {
              return (
                <Typography variant="body2">
                  Preview:{' '}
                  <Link
                    href={selected.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selected.previewUrl}
                  </Link>
                </Typography>
              );
            }
            if (selected.previewState === 'stale') {
              return (
                <Typography variant="body2" color="error">
                  The available preview belongs to an older version. Publishing
                  is blocked until the current preview is ready.
                </Typography>
              );
            }
            return (
              <Typography variant="body2" color="textSecondary">
                No preview is available yet — the deploy may still be running.
                Check back in a minute.
              </Typography>
            );
          })()}
        </Box>
      )}
    </FormControl>
  );
};
